import { ReactNode, useCallback, useMemo, useState } from "react"

import { useLocalStorage } from "@uidotdev/usehooks"

import { initialState, LaunchWebSocketParams, WebSocketEntry, WSDispatchContext, WSStateContext } from "./WSContext.context"

interface WSContextProviderProps {
    children: ReactNode
}

export function WSContextProvider(props: WSContextProviderProps) {
    const { children } = props

    const [localState, _] = useLocalStorage("authentication")
    const [webSockets, setWebSockets] = useState<Record<string, WebSocketEntry>>(initialState.webSockets)

    const removeWebSocket = useCallback((id: string) => {
        setWebSockets(previousState => {
            if (!previousState[id]) {
                return previousState
            }
            const { [id]: _removedWebSocket, ...remainingWebSockets } = previousState
            return remainingWebSockets
        })
    }, [])

    const setWebSocketProgress = useCallback((id: string, progress: number) => {
        setWebSockets(previousState => {
            if (!previousState[id]) {
                return previousState
            }
            return {
                ...previousState,
                [id]: {
                    ...previousState[id],
                    progress,
                },
            }
        })
    }, [])

    const closeWebSocket = useCallback((id: string) => {
        setWebSockets(previousState => {
            previousState[id]?.webSocket.close()
            return previousState
        })
    }, [])

    const launchWebSocket = useCallback((params: LaunchWebSocketParams) => {
        const { id, url, protocols, onOpen, onMessage, onClose } = params

        if (webSockets[id]) {
            return
        }

        const authUrl = `${url}?token=${localState?.["accessToken"]}`
        const webSocket = protocols ? new WebSocket(authUrl, protocols) : new WebSocket(authUrl)

        webSocket.onopen = (event) => {
            setWebSockets(previousState => ({
                ...previousState,
                [id]: {
                    ...previousState[id],
                    status: "open",
                },
            }))
            onOpen?.(event, webSocket)
        }

        webSocket.onmessage = (event) => {
            onMessage?.(event, webSocket)
        }

        webSocket.onclose = (event) => {
            onClose?.(event, webSocket)
            removeWebSocket(id)
        }

        webSocket.onerror = (event) => {
            console.error(`WebSocket error for id: ${id}`, event)
            webSocket.close()
        }

        setWebSockets(previousState => ({
            ...previousState,
            [id]: {
                id,
                webSocket,
                status: "connecting",
                progress: 0,
            },
        }))
    }, [webSockets, removeWebSocket, localState])

    const stateValue = useMemo(() => {
        return { webSockets }
    }, [webSockets])

    const dispatchValue = useMemo(() => {
        return { launchWebSocket, closeWebSocket, setWebSocketProgress }
    }, [launchWebSocket, closeWebSocket, setWebSocketProgress])

    return (
        <WSStateContext.Provider value={stateValue}>
            <WSDispatchContext.Provider value={dispatchValue}>
                {children}
            </WSDispatchContext.Provider>
        </WSStateContext.Provider>
    )
}
